import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FloatingActionButton, FloatingActionButtonService} from '@mydocma/component-library/src/lib/floating-action-button';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject} from 'rxjs';
import {FaultsImportService} from './faults-import.service';
import {getIosOsMajorVersion, getSafariMajorVersion, isIosDevice} from '@docma-mm-core/utils/utils';
import {FaultsImportStepperComponent} from './faults-import-stepper/faults-import-stepper.component';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {FaultsImportStepperService} from './faults-import-stepper/services/fault-import-stepper.service';
import {ValidateImportService} from './faults-import-stepper/services/validate-import.service';
import {SelectFileService} from './faults-import-stepper/services/select-file.service';
import {BaseImportService} from './faults-import-stepper/services/base-import.service';
import {PreviewImportService} from './faults-import-stepper/services/preview-import.service';
import {previewImportStep3} from './faults-import-stepper/faults-import-stepper.config';
import {MmTranslateService} from '../../mm-translate.service';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-faults-import-dialog',
  templateUrl: './mm-faults-import-dialog.component.html',
  styleUrls: ['./mm-faults-import-dialog.component.scss'],
  providers: [FaultsImportService,
    FaultsImportStepperService,
    SelectFileService,
    BaseImportService,
    ValidateImportService,
    PreviewImportService]
})
export class MmFaultsImportDialogComponent implements OnInit, AfterViewInit {

  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: {},
              private readonly dialogRef: MatDialogRef<MmFaultsImportDialogComponent>,
              private readonly fabService: FloatingActionButtonService,
              private translateService: MmTranslateService,
              protected readonly faultsImportService: FaultsImportService,
              public readonly importStepperService: FaultsImportStepperService) {

    this.engineMajorVersion = getSafariMajorVersion();
    this.iosOsMajorVersion = getIosOsMajorVersion();
    this.isAnimationUnsupportedDevice = this.computeIsAnimationUnsupportedDevice();

  }

  private static readonly ANIMATION_UNSUPPORTED_ENGINE_VERSIONS: number[] = [26];

  fabButtonSubject = new BehaviorSubject<FloatingActionButton>(null);
  fabButton$ = this.fabButtonSubject.asObservable();

  @ViewChild('stepperComponent', { static: false })
  private stepperComponent: FaultsImportStepperComponent;

  public isAnimationUnsupportedDevice: boolean;
  public readonly engineMajorVersion: number | null;
  public iosOsMajorVersion: number | null;

  public nextButtonText = 'MM.fault.import.next.button';

  async ngOnInit() {
    await this.faultsImportService.initialize();

    this.importStepperService.stepperService.currentStep$
      .pipe(untilDestroyed(this))
      .subscribe(step => {
        this.nextButtonText = (step.config.name === previewImportStep3.name ? 'MM.fault.import.buttonNext.import' : 'MM.fault.import.buttonNext.next');
      });

    const structureCodeField = this.faultsImportService.fieldDict.Structure_Name1;
    const structureCodePathField = this.faultsImportService.fieldDict.Structure_ShortPath;
    const structureDescriptionField = this.faultsImportService.fieldDict.Structure;
    const structureDisplayPathField = this.faultsImportService.fieldDict.Structure_DisplayPath;

    const fabButton = {
      id: FabButtonsEnum.template,
      floatingDisabled: false,
      text: 'MM.fault.import.create.button',
      items: [
        {
          id: FabButtonsEnum.templateByCode,
          text: this.translateService.instant('MM.fault.import.create.byCode', { structureFieldName: (structureCodeField?.Project ?? structureCodeField?.Global) }),
          class: 'fi-fab-button-item',
        } as any,
        {
          id: FabButtonsEnum.templateByCodePath,
          text: this.translateService.instant('MM.fault.import.create.byCodePath', { structureFieldName: (structureCodePathField?.Project ?? structureCodePathField?.Global) }),
          class: 'fi-fab-button-item'
        } as any,
        {
          id: FabButtonsEnum.templateByDescriptionPath,
          text: this.translateService.instant('MM.fault.import.create.byDescription', { structureFieldName: (structureDescriptionField?.Project ?? structureDescriptionField?.Global) }),
          class: 'fi-fab-button-item'
        } as any,
        {
          id: FabButtonsEnum.templateByDisplayPath,
          text: this.translateService.instant('MM.fault.import.create.byDisplayPath', { structureFieldName: (structureDisplayPathField?.Project ?? structureDisplayPathField?.Global) }),
          class: 'fi-fab-button-item'
        } as any
      ]
    } as FloatingActionButton;

    this.fabService.onFabButtonItemClick
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.faultsImportService.downloadTemplate(value.id as FabButtonsEnum);
      });

    this.fabButtonSubject.next(fabButton);
  }

  ngAfterViewInit() {
  }



  private computeIsAnimationUnsupportedDevice(): boolean {
    if (!isIosDevice()) {
      return false;
    }
    const versionToCheck = this.iosOsMajorVersion ?? this.engineMajorVersion;
    if (versionToCheck == null) {
      return false;
    }
    return MmFaultsImportDialogComponent.ANIMATION_UNSUPPORTED_ENGINE_VERSIONS.includes(versionToCheck);
  }

  onCancel() {
    this.dialogRef.close();
  }

  async onNext() {
    if (this.importStepperService.stepperService.currStep.config.name === previewImportStep3.name) {
      await this.faultsImportService.import();
      this.dialogRef.close();
    }
    else {
      await this.importStepperService.stepperService.nextStep();
    }
  }
}

export enum FabButtonsEnum {
  template = 'template',
  templateByCode = 'templateByCode',
  templateByCodePath = 'templateByCodePath',
  templateByDescriptionPath = 'templateByDescriptionPath',
  templateByDisplayPath = 'templateByDisplayPath'
}

